import { authFetch, authStore, logIn } from '@addictivegroup/addictive-auth'
import { onSessionLost, refreshXsrf } from './http.js'

/**
 * A session Core no longer takes, while the network works (D-246): straight to AddZone's sign-in
 * and back to the same page — no page in between to press. What waits in the queue stays on the
 * device and goes once signed in again.
 *
 * Not every refusal is a lost session: a write refused for its XSRF token (419) while the session
 * lives gets a fresh token and the queue goes again, without signing in. Never round and round: a
 * second sign-in asked within a minute shows the sign-in page instead.
 */
const LOOP_KEY = 'addcrm:reauth-at'
let handling = null
let retriedAt = 0

/** Whether Core still knows this session: true, false, or null when it cannot be asked. */
async function alive() {
  try {
    const res = await authFetch('/api/v1/me', { method: 'GET' })
    if (res.ok) return true
    return res.status === 401 || res.status === 419 ? false : null
  } catch {
    return null
  }
}


export function installReauth({ router, offline, session, notify }) {
  async function handle() {
    // Not signed in yet (the first visit): the sign-in page, as the router's guard shows it.
    if (!session.me) return
    const state = await alive()
    if (state === null) return
    if (state) {
      // Signed in still: the token was stale. Once a minute at most, so a refusal of another kind
      // does not go round.
      if (Date.now() - retriedAt < 60000) return
      retriedAt = Date.now()
      await refreshXsrf()
      offline.sync({ force: true }).catch(() => null)
      return
    }
    authStore.clear()
    // The device's data and its queue stay (no wipe): they go after signing in.
    await session.forget()
    const back = router.currentRoute.value.fullPath
    let last = 0
    try {
      last = Number(window.sessionStorage.getItem(LOOP_KEY) || 0)
      window.sessionStorage.setItem(LOOP_KEY, String(Date.now()))
    } catch {
      // private window: the guard of this page load only
    }
    if (Date.now() - last < 60000) {
      router.push({ name: 'openid-login', query: { redirect: back } })
      return
    }
    notify?.()
    logIn({ intendedPath: back })
  }
  onSessionLost(() => {
    handling ??= handle().finally(() => {
      handling = null
    })
  })
}
